import { computed, onBeforeUnmount, type Ref, ref, watch } from 'vue';

import { axios } from '@/helpers/axios';

const MINIMUM_QUERY_LENGTH = 3;

const DEBOUNCE_MS = 400;

interface AutocompleteResponse {
  suggestions: string[];
}

export function useKeywordAutocomplete(
  keyword: Ref<string>,
  marketplace: Ref<string>,
  country: Ref<string>,
  enabled: Ref<boolean> = ref(true),
) {
  const suggestions = ref<string[]>([]);
  const activeIndex = ref(-1);
  const isOpen = ref(false);
  let requestController: AbortController | null = null;
  let requestSequence = 0;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;

  const normalizedKeyword = computed(() => keyword.value.trim().replace(/\s+/g, ' ').toLowerCase());

  function close(): void {
    isOpen.value = false;
    activeIndex.value = -1;
  }

  function cancelRequest(): void {
    requestSequence += 1;
    requestController?.abort();
    requestController = null;
  }

  async function loadSuggestions(): Promise<void> {
    cancelRequest();

    if (!enabled.value || normalizedKeyword.value.length < MINIMUM_QUERY_LENGTH || !marketplace.value || !country.value) {
      suggestions.value = [];
      close();
      return;
    }

    const sequence = requestSequence;
    requestController = new AbortController();

    try {
      const response = await axios.get<AutocompleteResponse>('/keyword-tool/autocomplete', {
        params: {
          keyword: normalizedKeyword.value,
          marketplace: marketplace.value.toLowerCase(),
          country: country.value.toLowerCase(),
        },
        signal: requestController.signal,
      });

      if (sequence !== requestSequence) return;

      suggestions.value = response.data.suggestions.slice(0, 5);
      activeIndex.value = -1;
      isOpen.value = suggestions.value.length > 0;
    } catch {
      if (sequence !== requestSequence) return;

      suggestions.value = [];
      close();
    }
  }

  function cancelDebounce(): void {
    if (debounceTimer !== null) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
  }

  function debounceLoad(): void {
    cancelDebounce();
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      void loadSuggestions();
    }, DEBOUNCE_MS);
  }

  watch([normalizedKeyword, marketplace, country, enabled], () => {
    cancelDebounce();
    cancelRequest();
    suggestions.value = [];
    close();

    if (enabled.value && normalizedKeyword.value.length >= MINIMUM_QUERY_LENGTH) {
      debounceLoad();
    }
  });

  function moveActive(delta: 1 | -1): void {
    if (!suggestions.value.length) return;

    isOpen.value = true;
    activeIndex.value = activeIndex.value < 0
      ? (delta === 1 ? 0 : suggestions.value.length - 1)
      : (activeIndex.value + delta + suggestions.value.length) % suggestions.value.length;
  }

  function reopen(): void {
    if (suggestions.value.length) isOpen.value = true;
  }

  function handleKeydown(
    event: KeyboardEvent,
    submitTypedKeyword: () => void,
    selectSuggestion: (suggestion: string) => void,
  ): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      moveActive(event.key === 'ArrowDown' ? 1 : -1);
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }

    if (event.key === 'Tab') {
      close();
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const activeSuggestion = suggestions.value[activeIndex.value];
      activeSuggestion ? selectSuggestion(activeSuggestion) : submitTypedKeyword();
    }
  }

  onBeforeUnmount(() => {
    cancelDebounce();
    cancelRequest();
  });

  return {
    activeIndex,
    close,
    handleKeydown,
    isOpen,
    moveActive,
    reopen,
    suggestions,
  };
}
